import type { FC, ReactNode } from 'react'

import { Clapperboard, Heart, LayoutGrid, Palette, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface LayoutOption {
    name: string
    panels: string[]
    favorite?: boolean
    selected?: boolean
}

// Each layout is a set of polygons that tile a 40 x 22 frame; the dark stroke draws the gutters.
const OPTIONS: LayoutOption[] = [
    { name: 'Samurai Cut', favorite: true, panels: ['0,0 26,0 14,22 0,22', '26,0 40,0 40,22 14,22'] },
    { name: 'Sentinel', panels: ['0,0 24,0 24,22 0,22', '24,0 40,0 40,11 24,11', '24,11 40,11 40,22 24,22'] },
    { name: 'Infinite Escape', panels: ['0,0 40,0 40,5 0,9', '0,9 40,5 40,14 0,17', '0,17 40,14 40,22 0,22'] },
    { name: 'Zen Lightning', panels: ['0,0 18,0 24,11 16,22 0,22', '18,0 40,0 40,22 16,22 24,11'] },
    { name: 'Cinema Horizon', panels: ['0,0 40,0 40,10 0,10', '0,10 13,10 13,22 0,22', '13,10 27,10 27,22 13,22', '27,10 40,10 40,22 27,22'] },
    { name: 'Pure Dynamism', favorite: true, selected: true, panels: ['0,0 25,0 21,10.6 0,12', '25,0 40,0 40,9 21,10.6', '0,12 21,10.6 17,22 0,22', '21,10.6 40,9 40,22 17,22'] },
    { name: 'Broken Crystal', panels: ['0,0 16,0 10,13 0,8', '16,0 40,0 28,9 10,13', '0,8 10,13 6,22 0,22', '10,13 28,9 40,0 40,22 6,22'] },
    { name: 'Shonen Legend', panels: ['0,0 12,0 9,22 0,22', '12,0 30,0 32,22 9,22', '30,0 40,0 40,22 32,22'] },
    { name: 'Avant-Garde', panels: ['0,0 15,0 15,14 0,14', '15,0 40,0 40,8 15,8', '15,8 40,8 40,22 15,22', '0,14 15,14 15,22 0,22'] },
    { name: 'Epic Climax', panels: ['0,0 40,0 40,10 0,14', '0,14 22,11.8 20,22 0,22', '22,11.8 40,10 40,22 20,22'] }
]

const LayoutPickerScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_0%,#0f2a3d_0%,#081420_55%,#050b14_100%)] px-[5.5cqw] py-[2.6cqw] text-white'>
            <div className='flex items-center justify-between'>
                <div className='flex items-center gap-[1.4cqw]'>
                    <span className='grid size-[4.4cqw] place-items-center rounded-[1cqw] bg-cyan-400/15 ring-1 ring-cyan-300/40'>
                        <LayoutGrid className='size-[2.6cqw] text-cyan-300' />
                    </span>

                    <span className='text-[2.4cqw] font-semibold tracking-[-0.01em]'>
                        Choose your screen
                    </span>
                </div>

                <span className='grid size-[3.6cqw] place-items-center rounded-full bg-white/10'>
                    <X className='size-[2cqw] text-white/80' />
                </span>
            </div>

            <div className='mt-[1.8cqw] grid grid-cols-3 gap-[1cqw] rounded-[1.2cqw] bg-white/5 p-[0.6cqw] text-[1.6cqw] font-semibold'>
                <span className='flex items-center justify-center gap-[0.8cqw] rounded-[0.9cqw] py-[0.9cqw] text-white/75'>
                    <Clapperboard className='size-[1.9cqw]' />
                    Basics
                </span>

                <span className='flex items-center justify-center gap-[0.8cqw] rounded-[0.9cqw] bg-cyan-400/15 py-[0.9cqw] text-cyan-200 ring-1 ring-cyan-300/70 shadow-[0_0_2cqw_rgb(34_211_238/0.35)]'>
                    <Palette className='size-[1.9cqw]' />
                    Creative
                </span>

                <span className='flex items-center justify-center gap-[0.8cqw] rounded-[0.9cqw] py-[0.9cqw] text-white/75'>
                    <Heart className='size-[1.9cqw] text-pink-400' fill='currentColor' />
                    Favorites
                </span>
            </div>

            <div className='mt-[1.8cqw] grid grid-cols-5 gap-[1.2cqw]'>
                {OPTIONS.map((option) => (
                    <div
                        key={option.name}
                        className={cn(
                            'relative rounded-[1cqw] p-[0.8cqw] ring-1',
                            option.selected
                                ? 'bg-cyan-400/10 ring-cyan-300 shadow-[0_0_2.2cqw_rgb(34_211_238/0.45)]'
                                : 'bg-white/[0.04] ring-white/10'
                        )}
                    >
                        <svg viewBox='0 0 40 22' className='block w-full' aria-hidden='true'>
                            {option.panels.map((points) => (
                                <polygon
                                    key={points}
                                    points={points}
                                    fill={option.selected ? '#22d3ee' : '#94a3b8'}
                                    fillOpacity={option.selected ? 0.9 : 0.75}
                                    stroke='#0b1724'
                                    strokeWidth='1.4'
                                    strokeLinejoin='round'
                                />
                            ))}
                        </svg>

                        <span className={cn('mt-[0.7cqw] block truncate text-center text-[1.3cqw] font-semibold', option.selected ? 'text-cyan-200' : 'text-white/85')}>
                            {option.name}
                        </span>

                        <span className='absolute right-[0.5cqw] top-[0.5cqw] grid size-[2cqw] place-items-center rounded-full bg-black/60'>
                            <Heart
                                className={cn('size-[1.2cqw]', option.favorite ? 'text-pink-400' : 'text-white/70')}
                                fill={option.favorite ? 'currentColor' : 'none'}
                            />
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default LayoutPickerScreen
